import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ForfeitureReasonClient, ForfeitureReasonDTO } from '../services/apiClient';
import { API_BASE_URL, MAX_DROPDOWN_PAGE_SIZE } from '../config/config';
import axiosInstance from '../services/axiosInstance';

const apiClient = new ForfeitureReasonClient(API_BASE_URL, axiosInstance);

// Query hook - Get all forfeiturereasons for dropdowns (cached 4 hours)
export const useForfeitureReasons = () => {
  return useQuery({
    queryKey: ['forfeiturereasons'],
    queryFn: async () => {
      const response = await apiClient.searchForfeitureReasons({
        page: 1,
        pageSize: MAX_DROPDOWN_PAGE_SIZE,
        sortBy: 'Title',
      });
      return response?.data?.results || [];
    },
    staleTime: 4 * 60 * 60 * 1000, // 4 hours
    refetchOnReconnect: true,
  });
};

// Direct API search (no caching) - for admin tables with server-side paging
export const useForfeitureReasonSearch = () => {
  const search = async (params: {
    page: number;
    pageSize: number;
    sortBy?: string;
    sortDescending?: boolean;
    filters?: any;
  }) => {
    const response = await apiClient.searchForfeitureReasons({
      page: params.page,
      pageSize: params.pageSize,
      sortBy: params.sortBy || 'CreatedUtc',
      sortDescending: params.sortDescending ?? true,
      filters: params.filters || {},
    });
    return {
      results: response?.data?.results || [],
      totalCount: response?.data?.totalCount || 0,
    };
  };

  return { search };
};

// Query hook - Get single forfeitureReason by ID
export const useForfeitureReasonById = (id: string) => {
  return useQuery({
    queryKey: ['forfeiturereasons', id],
    queryFn: async () => {
      const response = await apiClient.getForfeitureReasonById(id);
      return response?.data;
    },
    enabled: !!id,
    staleTime: Infinity,
    refetchOnReconnect: true,
  });
};

// Mutation hook - Create forfeitureReason
export const useCreateForfeitureReason = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ForfeitureReasonDTO) => {
      await apiClient.createForfeitureReason(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['forfeiturereasons'] });
    },
  });
};

// Mutation hook - Update forfeitureReason
export const useUpdateForfeitureReason = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: ForfeitureReasonDTO }) => {
      await apiClient.updateForfeitureReason(id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['forfeiturereasons'] });
    },
  });
};

// Mutation hook - Delete forfeitureReason
export const useDeleteForfeitureReason = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.deleteForfeitureReason(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['forfeiturereasons'] });
    },
  });
};
